import React, {createContext, useCallback, useContext} from "react"
import {useStreamReducer} from "@/hooks/useStreamReducer"
import {appReducer, actionFactory} from "@/api/app/AppReducer"
import {actionFactory as identityActionFactory} from "@/api/identity/IdentityReducer"
import {actionFactory as incomeActionFactory} from "@/api/income/IncomeReducer"
import appEpics from "./AppEpics"
import {generateTheme} from "@/theme/theme"
import {getDeviceInfo} from '@/lib/deviceInfo'
import useOnmount from "@/hooks/useOnMount"
import Loading from "@/components/Loading"

const AppContext = createContext(undefined)
export const useAppContext = () => useContext(AppContext)

function AppContextProvider({children, appApi, identityApi, incomeApi}) {
  const deviceInfo = getDeviceInfo()

  const [appState, dispatch, ready] = useStreamReducer(
    appReducer,
    {
      exchangeTokenLoading: false,
      tenantConfig: undefined,
      apiKey: undefined,
      token: undefined,
      noTokenFound: false,
      tokenState: undefined,
      identityState: {
        faceDetectionErrorCodes: [],
        faceHintCount: 0,
        perfectHintCount: 0,
        timeoutPageCount: 0,
        attempts: []
      },
      incomeState: {},
      theme: generateTheme(),
      deviceInfo,
      timingMap: {}
    },
    appEpics,
    {appApi, identityApi, incomeApi}
  )

  const onTokenUpdated = useCallback((token) => {
    dispatch(actionFactory.onTokenUpdated({token}))
  }, [dispatch])

  const onTokenSubmitted = useCallback(() => {
    dispatch(actionFactory.onTokenSubmitted())
  }, [dispatch])

  const onNoTokenFound = useCallback(() => {
    dispatch(actionFactory.onNoTokenFound())
  }, [dispatch])

  const onTenantConfigUpdated = useCallback((tenantConfig) => {
    dispatch(actionFactory.onTenantConfigUpdated({tenantConfig}))
  }, [dispatch])

  const onUploadIncomeDocument = useCallback((payload) => {
    dispatch(incomeActionFactory.uploadIncomeDocument(payload))
  }, [dispatch])

  const onResetUploadIncomeDocumentState = useCallback(() => {
    dispatch(incomeActionFactory.resetUploadIncomeDocumentState())
  }, [dispatch])

  const onFilesValidated = useCallback((payload) => {
    dispatch(incomeActionFactory.filesValidated(payload))
  }, [dispatch])

  const onFileSizeExceeded = useCallback((payload) => {
    dispatch(incomeActionFactory.fileSizeExceeded(payload))
  }, [dispatch])

  const onMimeTypeValidationFailed = useCallback((payload) => {
    dispatch(incomeActionFactory.mimeTypeValidationFailed(payload))
  }, [dispatch])

  const onFileSignatureValidationFailed = useCallback((payload) => {
    dispatch(incomeActionFactory.fileSignatureValidationFailed(payload))
  }, [dispatch])

  const onDuplicateFileDetected = useCallback(() => {
    dispatch(incomeActionFactory.duplicateFileDetected())
  }, [dispatch])

  const onUploadMethodSelected = useCallback((payload) => {
    dispatch(incomeActionFactory.uploadMethodSelected(payload))
  }, [dispatch])

  const onFileRemovedAfterSelection = useCallback(() => {
    dispatch(incomeActionFactory.fileRemovedAfterSelection())
  }, [dispatch])

  const onBankSelected = useCallback((bankName) => {
    dispatch(incomeActionFactory.bankSelected(bankName))
  }, [dispatch])

  const requestConsent = useCallback((payload) => {
    dispatch(incomeActionFactory.requestConsent(payload))
  }, [dispatch])

  const pollConsentStatus = useCallback((payload) => {
    dispatch(incomeActionFactory.pollConsentStatus(payload))
  }, [dispatch])

  const resetConsentState = useCallback(() => {
    dispatch(incomeActionFactory.resetConsentState())
  }, [dispatch])

  const uploadSecureCaptureSelfie = useCallback((payload) => {
    dispatch(identityActionFactory.uploadSecureCaptureSelfie(payload))
  }, [dispatch])

  const onTrySecureCaptureSelfieAgainClicked = useCallback(() => {
    dispatch(identityActionFactory.onTrySecureCaptureSelfieAgainClicked())
  }, [dispatch])

  const onUnexpectedErrorTryAgainClicked = useCallback(() => {
    dispatch(identityActionFactory.unexpectedErrorTryAgainClicked())
  }, [dispatch])

  const onFaceDetectionTextChanged = useCallback((payload) => {
    dispatch(identityActionFactory.onFaceDetectionTextChanged(payload))
  }, [dispatch])

  const onFaceHintShown = useCallback(() => {
    dispatch(identityActionFactory.faceHintShown())
  }, [dispatch])

  const onPerfectHintShown = useCallback(() => {
    dispatch(identityActionFactory.perfectHintShown())
  }, [dispatch])

  const onBlockedCameraDetected = useCallback(() => {
    dispatch(actionFactory.blockedCameraDetected({token: appState.token}))
  }, [dispatch, appState.token])

  const onCaptureLiveFaceFailedToLoad = useCallback(() => {
    dispatch(actionFactory.captureLiveFaceFailedToLoad())
  }, [dispatch])

  const onCaptureLiveFaceTimedOut = useCallback(() => {
    dispatch(actionFactory.captureLiveFaceTimedOut())
  }, [dispatch])

  const onUnexpectedCaptureLiveFaceError = useCallback((payload) => {
    dispatch(actionFactory.onUnexpectedCaptureLiveFaceError(payload))
  }, [dispatch])

  const onNoConfigurationProfileFound = useCallback((payload) => {
    dispatch(actionFactory.noConfigurationProfileFound(payload))
  }, [dispatch])

  const onCameraInitializationTimedOut = useCallback(() => {
    dispatch(actionFactory.cameraInitializationTimedOut())
  }, [dispatch])

  const onCameraMounted = useCallback(() => {
    dispatch(actionFactory.cameraMounted())
  }, [dispatch])

  const onCameraUnmounted = useCallback(() => {
    dispatch(actionFactory.cameraUnmounted())
  }, [dispatch])

  const onCameraOpening = useCallback(() => {
    dispatch(actionFactory.cameraOpening())
  }, [dispatch])

  const onCameraOpened = useCallback(() => {
    dispatch(actionFactory.cameraOpened())
  }, [dispatch])

  const onCaptureJourneyStarted = useCallback(() => {
    dispatch(actionFactory.captureJourneyStarted())
  }, [dispatch])

  const onFaceCaptureInitStarted = useCallback(() => {
    dispatch(actionFactory.faceCaptureInitStarted())
  }, [dispatch])

  const onFaceCaptureInitialized = useCallback(() => {
    dispatch(actionFactory.faceCaptureInitialized())
  }, [dispatch])

  const onFaceCaptureCapturing = useCallback(() => {
    dispatch(actionFactory.faceCaptureCapturing())
  }, [dispatch])

  const onFaceCaptureCaptured = useCallback(() => {
    dispatch(actionFactory.faceCaptureCaptured())
  }, [dispatch])

  const onResetCameraInitialization = useCallback(() => {
    dispatch(actionFactory.resetCameraInitialization())
  }, [dispatch])

  const onCameraErrorReceived = useCallback((payload) => {
    dispatch(actionFactory.cameraErrorReceived(payload))
  }, [dispatch])

  const onWatchCameraPermission = useCallback(() => {
    dispatch(actionFactory.watchCameraPermission())
  }, [dispatch])

  const onUnwatchCameraPermission = useCallback(() => {
    dispatch(actionFactory.unwatchCameraPermission())
  }, [dispatch])

  const onManualCaptureSelected = useCallback(() => {
    dispatch(identityActionFactory.manualCaptureSelected())
  }, [dispatch])

  const onAutoCaptureSelected = useCallback(() => {
    dispatch(identityActionFactory.autoCaptureSelected())
  }, [dispatch])

  const onSdkLoadStarted = useCallback(() => {
    dispatch(actionFactory.sdkLoadStarted())
  }, [dispatch])

  const onSdkLoadCompleted = useCallback(() => {
    dispatch(actionFactory.sdkLoadCompleted())
  }, [dispatch])

  const onSdkLoadFailed = useCallback((payload) => {
    dispatch(actionFactory.sdkLoadFailed(payload))
  }, [dispatch])

  const onCdnLoadRetryAttempt = useCallback((payload) => {
    dispatch(actionFactory.cdnLoadRetryAttempt(payload))
  }, [dispatch])

  const onCdnLoadSuccess = useCallback(() => {
    dispatch(actionFactory.cdnLoadSuccess())
  }, [dispatch])

  const onCdnLoadFailedAfterRetries = useCallback((payload) => {
    dispatch(actionFactory.cdnLoadFailedAfterRetries(payload))
  }, [dispatch])

  const consentedAccounts = useCallback((payload) => {
    dispatch(incomeActionFactory.consentedAccounts(payload))
  }, [dispatch])

  const selectAccount = useCallback((payload) => {
    dispatch(incomeActionFactory.selectAccount(payload))
  }, [dispatch])

  const resetAccountSelectionState = useCallback(() => {
    dispatch(incomeActionFactory.resetAccountSelectionState())
  }, [dispatch])

  const onUserIdentifiedLandingBypassSelected = useCallback(() => {
    dispatch(incomeActionFactory.onUserIdentifiedLandingBypassSelected())
  }, [dispatch])

  const onRecordedIdentifierSelected = useCallback(() => {
    dispatch(actionFactory.recordedIdentifierSelected())
  }, [dispatch])

  const onManualIdentifierSelected = useCallback(() => {
    dispatch(actionFactory.manualIdentifierSelected())
  }, [dispatch])

  const onCheckDeploymentVersion = useCallback(() => {
    dispatch(actionFactory.checkDeploymentVersion())
  }, [dispatch])

  useOnmount(() => dispatch(actionFactory.onAppContextMounted()))

  return (
    <AppContext.Provider value={{
      appState: {...appState},
      onTokenUpdated,
      onTokenSubmitted,
      onNoTokenFound,
      uploadSecureCaptureSelfie,
      onTrySecureCaptureSelfieAgainClicked,
      onUnexpectedErrorTryAgainClicked,
      onCameraMounted,
      onCameraUnmounted,
      onCameraOpening,
      onCameraOpened,
      onCaptureJourneyStarted,
      onFaceCaptureInitStarted,
      onFaceCaptureInitialized,
      onFaceCaptureCapturing,
      onFaceCaptureCaptured,
      onFaceDetectionTextChanged,
      onFaceHintShown,
      onPerfectHintShown,
      onUploadIncomeDocument,
      onResetUploadIncomeDocumentState,
      onFilesValidated,
      onFileSizeExceeded,
      onMimeTypeValidationFailed,
      onFileSignatureValidationFailed,
      onDuplicateFileDetected,
      onUploadMethodSelected,
      onFileRemovedAfterSelection,
      onBankSelected,
      requestConsent,
      pollConsentStatus,
      resetConsentState,
      onTenantConfigUpdated,
      onBlockedCameraDetected,
      onCaptureLiveFaceFailedToLoad,
      onCaptureLiveFaceTimedOut,
      onUnexpectedCaptureLiveFaceError,
      onNoConfigurationProfileFound,
      onCameraInitializationTimedOut,
      onResetCameraInitialization,
      onCameraErrorReceived,
      onWatchCameraPermission,
      onUnwatchCameraPermission,
      onManualCaptureSelected,
      onAutoCaptureSelected,
      onSdkLoadStarted,
      onSdkLoadCompleted,
      onSdkLoadFailed,
      onCdnLoadRetryAttempt,
      onCdnLoadSuccess,
      onCdnLoadFailedAfterRetries,
      consentedAccounts,
      selectAccount,
      resetAccountSelectionState,
      onUserIdentifiedLandingBypassSelected,
      onRecordedIdentifierSelected,
      onManualIdentifierSelected,
      onCheckDeploymentVersion
    }}>
      {appState.loadApiKeyInProgress === false && appState.tenantConfig && ready
        ? children
        : <Loading loadingMessage={"Loading..."} withCounter={false}/>}
    </AppContext.Provider>
  )
}

export default AppContextProvider